/* eslint-disable react-hooks/exhaustive-deps */
import { motion } from "framer-motion";
import { FaLinkedin, FaSpinner } from "react-icons/fa";
import Footer from "../components/Footer";
import React, { useState, useEffect } from "react";
import axios from "axios";

const OurTeam = () => {
  const [teams, setTeams] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = `${process.env.REACT_APP_API_BASE_URL}/UIteamMembers`;
  const TEAMS_API_URL = `${process.env.REACT_APP_API_BASE_URL}/UIteamMasters`;
  const API_MEDIA = `${process.env.REACT_APP_API_BASE_URL}/UImedia`;

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    },
    hover: {
      y: -10,
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    hover: {
      scale: 1.1,
      transition: {
        duration: 0.4,
        ease: "easeInOut"
      }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const textVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  // Fetch image securely
  const fetchImageWithAuth = async (imageId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_MEDIA}/${imageId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      return URL.createObjectURL(response.data);
    } catch (err) {
      console.error("Error fetching team member image:", err);
      return "/images/ourTeam-imgs/unknown.webp"; // Fallback image
    }
  };

  // Fetch teams
  const fetchTeams = async () => {
    try {
      const res = await axios.get(TEAMS_API_URL);
      const data = res.data.results || res.data || [];
      // Filter only active teams
      const activeTeams = data.filter(team => team.isActive !== false);
      setTeams(activeTeams);
      return activeTeams;
    } catch (error) {
      console.error("Error fetching teams:", error);
      throw error;
    }
  };

  // Fetch team members
  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch teams first
      const teamsData = await fetchTeams();
      
      // Fetch all team members
      const res = await axios.get(API_URL);
      const membersData = res.data.results || res.data || [];

      // Fetch images for each member and enrich with team data
      const membersWithImages = await Promise.all(
        membersData.map(async (member) => {
          let imageUrl = "/images/ourTeam-imgs/unknown.webp"; // Default fallback
          
          if (member.image?.id) {
            try {
              imageUrl = await fetchImageWithAuth(member.image.id);
            } catch (error) {
              console.error(`Failed to load image for ${member.name}:`, error);
            }
          }

          // Find the team data for this member
          const teamData = teamsData.find(team => 
            team.id === member.teamId || team.id === member.teamId?.id
          );

          return {
            id: member.id,
            name: member.name || "Unknown Member",
            role: member.role || "",
            image: imageUrl,
            linkedin: member.linkedin || "",
            bio: member.bio || "",
            teamId: member.teamId?.id || member.teamId,
            teamName: teamData?.teamName || "Unknown Team",
            teamData: teamData, // Store full team data
            isActive: member.isActive !== undefined ? member.isActive : true
          };
        })
      );

      // Filter only active members
      const activeMembers = membersWithImages.filter(member => member.isActive);
      setTeamMembers(activeMembers);

    } catch (err) {
      console.error("Error fetching team members:", err);
      setError("Failed to load team data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Group members by team
  const getMembersByTeam = () => {
    const grouped = {};
    
    teams.forEach(team => {
      grouped[team.id] = {
        teamName: team.teamName,
        members: teamMembers.filter(member => 
          member.teamId === team.id || member.teamId?.id === team.id
        )
      };
    });

    return grouped;
  };

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const TeamSection = ({ title, members }) => {
    if (!members || members.length === 0) return null;

    return (
      <motion.div
        className="w-full max-w-7xl mx-auto mb-16 px-4"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
      >
        <motion.h2 
          className="text-3xl font-bold text-yellow-400 mb-8 text-center"
          variants={titleVariants}
        >
          {title}
        </motion.h2>
        
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          variants={containerVariants}
        >
          {members.map((member, index) => (
            <motion.div
              key={member.id || index}
              className="bg-gray-600 p-6 rounded-lg shadow-lg flex flex-col h-full relative overflow-hidden group"
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              whileHover="hover"
            >
              {/* Background gradient on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="flex-grow relative z-10">
                <motion.div
                  className="relative mx-auto mb-4 w-32 h-32"
                  variants={imageVariants}
                >
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover rounded-full shadow-lg border-4 border-yellow-400/30 group-hover:border-yellow-400/60 transition-all duration-300"
                    onError={(e) => {
                      e.target.src = "/images/ourTeam-imgs/unknown.webp";
                    }}
                  />
                  {/* Shine effect on image hover */}
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                </motion.div>
                
                <motion.h2 
                  className="text-xl font-bold text-white text-center mb-2"
                  variants={textVariants}
                >
                  {member.name}
                </motion.h2>
                
                {member.role && (
                  <motion.p 
                    className="text-yellow-400 text-center text-sm mb-3 font-medium"
                    variants={textVariants}
                  >
                    {member.role}
                  </motion.p>
                )}
                
                <motion.p 
                  className="text-gray-300 text-sm text-center leading-relaxed"
                  variants={textVariants}
                >
                  {member.bio}
                </motion.p>
              </div>
              
              <motion.div 
                className="mt-4 pt-4 text-center border-t border-gray-700 relative z-10"
                variants={itemVariants}
              >
                {member.linkedin ? (
                  <motion.a 
                    href={member.linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block"
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    <FaLinkedin className="text-2xl text-blue-400 hover:text-blue-300 transition-colors duration-300" />
                  </motion.a>
                ) : (
                  <span className="text-gray-400 text-sm">No LinkedIn</span>
                )}
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    );
  };

  // Render all team sections
  const renderTeamSections = () => {
    const membersByTeam = getMembersByTeam();
    
    return teams.map(team => {
      const teamMembers = membersByTeam[team.id]?.members || [];
      
      if (teamMembers.length === 0) return null;

      return (
        <TeamSection 
          key={team.id}
          title={team.teamName}
          members={teamMembers}
        />
      );
    }).filter(Boolean); // Remove null values
  };

  // Check if there are any members without teams
  const getMembersWithoutTeams = () => {
    return teamMembers.filter(member => {
      const hasTeam = teams.some(team => 
        team.id === member.teamId || team.id === member.teamId?.id
      );
      return !hasTeam;
    });
  };

  if (loading) {
    return (
      <motion.div 
        className="bg-gray-900 min-h-screen flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div 
          className="text-center"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <FaSpinner className="text-yellow-400 text-4xl mx-auto mb-4" />
          </motion.div>
          <motion.p 
            className="text-gray-300 text-lg"
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            Loading our team...
          </motion.p>
        </motion.div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div 
        className="bg-gray-900 min-h-screen flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div 
          className="text-center"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 10 }}
          >
            <div className="text-red-400 text-4xl mb-4">⚠️</div>
          </motion.div>
          <motion.p 
            className="text-gray-300 text-lg mb-4"
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {error}
          </motion.p>
          <motion.button
            onClick={fetchTeamMembers}
            className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 px-6 py-2 rounded-lg transition-colors font-semibold"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            Try Again
          </motion.button>
        </motion.div>
      </motion.div>
    );
  }

  const membersWithoutTeams = getMembersWithoutTeams();
  const hasTeamSections = renderTeamSections().length > 0;
  const hasAnyMembers = teamMembers.length > 0;

  return (
    <motion.div 
      className="bg-gray-900"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <div className="min-h-screen py-16">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <motion.h1 
            className="text-4xl md:text-5xl font-bold text-yellow-400 mb-4"
            variants={titleVariants}
          >
            Our Team
          </motion.h1>
          <motion.p 
            className="text-gray-300 max-w-3xl mx-auto px-4 text-lg"
            variants={textVariants}
          >
            Meet the dedicated individuals who drive our organization forward with their expertise and passion.
          </motion.p>
        </motion.div>

        {/* Display teams with their members */}
        {hasTeamSections && (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            {renderTeamSections()}
          </motion.div>
        )}

        {/* Display members without teams */}
        {membersWithoutTeams.length > 0 && (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <TeamSection 
              title="Team Members" 
              members={membersWithoutTeams} 
            />
          </motion.div>
        )}

        {/* Empty state */}
        {!hasAnyMembers && !loading && (
          <motion.div 
            className="text-center py-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div 
              className="text-gray-400 text-6xl mb-4"
              variants={itemVariants}
            >
              👥
            </motion.div>
            <motion.h3 
              className="text-xl font-semibold text-white mb-2"
              variants={itemVariants}
            >
              No Team Members Yet
            </motion.h3>
            <motion.p 
              className="text-gray-400"
              variants={itemVariants}
            >
              There are currently no active team members to display.
            </motion.p>
          </motion.div>
        )}

        {/* No teams but has members */}
        {!hasTeamSections && hasAnyMembers && (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <TeamSection 
              title="Our Team" 
              members={teamMembers} 
            />
          </motion.div>
        )}
      </div>
      
      {/* Footer with animation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <Footer />
      </motion.div>
    </motion.div>
  );
};

export default OurTeam;